import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import PageNavigation from "@/components/PageNavigation";
import SEOHead from "@/components/seo/SEOHead";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import juniorBianchim from "@/assets/junior-bianchim.jpg";
import postCoverScript from "@/assets/cover-script-de-vendas.jpg";
import scriptLigacao from "@/assets/script-vendas-ligacao.jpg";
import scriptRapport from "@/assets/script-vendas-rapport.jpg";
import postCoverEstrategias from "@/assets/cover-13-estrategias.jpg";
import postCoverColdcalling from "@/assets/cover-cold-calling.jpg";
import postCoverObjecoes from "@/assets/cover-objecoes-preco.jpg";
import {
  ArrowRight,
  Clock,
  User,
  BookOpen,
  CheckCircle,
  ChevronRight,
  Target,
  Zap,
  MessageSquare,
  PlayCircle,
  Handshake,
  HelpCircle,
  Download,
  AlertTriangle,
  Quote,
  Phone,
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";

const SCRIPT_TXT = `==========================================================
SCRIPT DE VENDAS PRONTO PARA PREENCHER
Bianchim Business | Vendas Trainer
bianchimbusiness.com.br
==========================================================

COMO USAR: substitua tudo que está entre [colchetes] pela
realidade da sua empresa. Leia em voz alta 3 vezes. Depois
guarde o papel: script é para treinar, não para ler.

----------------------------------------------------------
0) ANTES DA LIGAÇÃO (2 minutos)
----------------------------------------------------------
Empresa do contato: [_______________________]
Cargo do contato: [_______________________]
Gatilho/motivo do contato (notícia, vaga, indicação, site):
[_______________________________________________________]
Hipótese de dor: [_______________________________________]
Objetivo desta conversa (1 só): [________________________]

----------------------------------------------------------
1) ABERTURA (15 segundos) - roteiro de ligação comercial
----------------------------------------------------------
"Oi [NOME], aqui é [SEU NOME] da [SUA EMPRESA].
Liguei do nada, posso roubar 30 segundos e você me diz se
faz sentido continuar?"

(pausa - espere a resposta)

"Eu falo com [CARGO/SEGMENTO] que costumam travar em
[DOR PRINCIPAL]. Foi por isso que te procurei."

----------------------------------------------------------
2) RAPPORT (30 a 60 segundos)
----------------------------------------------------------
Referência específica: "Vi que vocês [FATO REAL SOBRE A
EMPRESA]. Isso normalmente vem junto com [CONSEQUÊNCIA].
Faz sentido no seu caso?"

Regras:
- Espelhe o ritmo de fala do cliente.
- Elogio só se for verdadeiro e específico.
- Fale menos de 40% do tempo.

----------------------------------------------------------
3) PERGUNTAS DE DESCOBERTA (o coração do script)
----------------------------------------------------------
Situação:
- "Como funciona hoje o processo de [ÁREA] aí?"
- "Quantas pessoas estão envolvidas nisso?"

Problema:
- "O que mais atrapalha nesse processo hoje?"
- "Com que frequência isso acontece?"

Implicação:
- "Quando isso acontece, o que deixa de ser feito?"
- "Quanto isso custa por mês, na sua estimativa?"

Necessidade / Solução:
- "Se você pudesse resolver só uma coisa disso, qual seria?"
- "O que mudaria no seu resultado se isso estivesse resolvido?"

Decisão:
- "Além de você, quem mais participa dessa decisão?"
- "Existe verba prevista ou precisaria ser aprovada?"
- "Qual o prazo ideal para isso estar rodando?"

----------------------------------------------------------
4) APRESENTAÇÃO (só depois das perguntas)
----------------------------------------------------------
"Pelo que você me contou, [REPETIR A DOR NAS PALAVRAS DELE].
O que a gente faz é [SOLUÇÃO EM 1 FRASE], e o efeito prático
costuma ser [RESULTADO MENSURÁVEL].
Um caso parecido: [CLIENTE/SEGMENTO] saiu de [ANTES] para
[DEPOIS] em [PRAZO]."

----------------------------------------------------------
5) OBJEÇÕES MAIS COMUNS
----------------------------------------------------------
"Está caro"
-> "Entendo. Caro comparado a quê? Quero te mostrar o custo
   de continuar como está: [NÚMERO DA IMPLICAÇÃO]."

"Vou pensar"
-> "Claro. Só para eu te ajudar melhor: o que exatamente
   ficou em aberto - preço, prazo ou confiança no resultado?"

"Já tenho fornecedor"
-> "Ótimo, sinal de que o tema é prioridade. Se hoje tem uma
   coisa que você mudaria nesse fornecedor, qual seria?"

"Me manda por e-mail"
-> "Mando agora. Só para o e-mail não virar spam: qual parte
   você quer que eu detalhe primeiro?"

Sua objeção nº1: [__________________________________]
Sua melhor resposta: [______________________________]

----------------------------------------------------------
6) FECHAMENTO E PRÓXIMO PASSO (sempre com data)
----------------------------------------------------------
"Faz sentido para você? Então proponho o seguinte:
[PRÓXIMO PASSO CONCRETO] na [DIA] às [HORA].
Fecho na sua agenda agora?"

Nunca encerre sem: data + hora + responsável + o que será
levado para a próxima conversa.

----------------------------------------------------------
7) FOLLOW-UP (mensagem curta, mesmo dia)
----------------------------------------------------------
"[NOME], obrigado pela conversa. Resumo do que combinamos:
1) [PONTO 1]  2) [PONTO 2]  3) [PRÓXIMO PASSO - DATA].
Se eu esqueci de algo, me corrige aqui mesmo."

==========================================================
Treine este script com personas de cliente em IA e receba
nota por critério: bianchimbusiness.com.br
==========================================================
`;

const ScriptDeVendasPronto = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [nome, setNome] = useState("");
  const [empresa, setEmpresa] = useState("");
  const [saving, setSaving] = useState(false);
  const [baixado, setBaixado] = useState(false);

  const baixarScript = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailLimpo = email.trim();
    if (!/^\S+@\S+\.\S{2,}$/.test(emailLimpo)) {
      toast({ title: "E-mail inválido", description: "Informe um e-mail válido para receber o script.", variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.from("leads_calculadora" as any).insert({
        email: emailLimpo.substring(0, 255),
        nome: nome.trim().substring(0, 100) || null,
        empresa: empresa.trim().substring(0, 100) || null,
        origem: 'script',
      } as any);
      if (error) console.error("Erro ao salvar lead:", error);
    } catch (err) {
      console.error("Erro ao salvar lead:", err);
    } finally {
      setSaving(false);
    }

    const blob = new Blob([SCRIPT_TXT], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "script-de-vendas-bianchim-business.txt";
    a.click();
    URL.revokeObjectURL(url);
    setBaixado(true);
    toast({ title: "Script liberado!", description: "O arquivo .txt foi baixado. Preencha os colchetes e treine em voz alta." });
  };

  const verdades = [
    {
      icon: Target,
      title: "Script não é texto para ler",
      text: "Script é estrutura de decisão. Ele define a ordem da conversa — abertura, rapport, perguntas, apresentação, objeção, próximo passo — e libera sua cabeça para ouvir. Vendedor que lê parece robô; vendedor sem estrutura vira palestrante.",
    },
    {
      icon: HelpCircle,
      title: "Um bom script tem mais perguntas do que afirmações",
      text: "Se o seu roteiro de ligação comercial tem três parágrafos sobre a empresa e duas perguntas, ele está invertido. A proporção saudável é o cliente falando ao menos 60% do tempo — e isso só acontece quando as perguntas estão escritas antes.",
    },
    {
      icon: Zap,
      title: "Script bom cabe em uma página",
      text: "Roteiro de 12 páginas não é usado por ninguém. O que funciona é uma folha com blocos curtos, gatilhos de pergunta e as três objeções que mais derrubam suas vendas. O resto é repertório — e repertório se treina, não se imprime.",
    },
    {
      icon: MessageSquare,
      title: "Script é vivo",
      text: "O script de prospecção que funcionava em janeiro pode estar morto em junho porque o mercado mudou de dor. Revise a cada ciclo: o que gerou reunião, qual frase travou a conversa, qual objeção nova apareceu.",
    },
  ];

  const blocos = [
    {
      bloco: "Abertura",
      tempo: "15s",
      objetivo: "Ganhar permissão para continuar",
      erroComum: "Começar com 'tudo bem?' e um discurso de 2 minutos sobre a empresa",
    },
    {
      bloco: "Rapport",
      tempo: "30–60s",
      objetivo: "Criar conexão real com um fato específico do cliente",
      erroComum: "Elogio genérico ('que empresa incrível!') que soa a script decorado",
    },
    {
      bloco: "Descoberta",
      tempo: "5–10 min",
      objetivo: "Encontrar dor, impacto e critério de decisão",
      erroComum: "Pular para a demonstração antes de saber o que dói",
    },
    {
      bloco: "Apresentação",
      tempo: "2–3 min",
      objetivo: "Conectar a solução às palavras que o cliente usou",
      erroComum: "Listar funcionalidades em vez de resultado mensurável",
    },
    {
      bloco: "Objeção",
      tempo: "2–5 min",
      objetivo: "Entender o que está por trás do 'não' antes de responder",
      erroComum: "Rebater na hora, com desconto, sem investigar a causa",
    },
    {
      bloco: "Próximo passo",
      tempo: "1 min",
      objetivo: "Sair com data, hora e responsável definidos",
      erroComum: "Encerrar com 'qualquer coisa me chama' — o famoso funil furado",
    },
  ];

  const perguntas = [
    {
      tipo: "Situação",
      cor: "from-primary/15 to-primary/5",
      exemplos: [
        "Como funciona hoje o processo de [área] aí dentro?",
        "Quem participa dessa rotina no dia a dia?",
      ],
      dica: "Faça poucas. Pesquise antes o que dá para descobrir sozinho.",
    },
    {
      tipo: "Problema",
      cor: "from-accent/15 to-accent/5",
      exemplos: [
        "O que mais atrapalha nesse processo hoje?",
        "Com que frequência isso acontece?",
      ],
      dica: "Aqui começa a venda. Silêncio depois da pergunta é ferramenta.",
    },
    {
      tipo: "Implicação",
      cor: "from-primary/15 to-accent/10",
      exemplos: [
        "Quando isso acontece, o que deixa de ser feito?",
        "Quanto isso custa por mês, na sua estimativa?",
      ],
      dica: "É a pergunta que transforma incômodo em urgência — e urgência em orçamento.",
    },
    {
      tipo: "Necessidade",
      cor: "from-accent/15 to-primary/10",
      exemplos: [
        "Se desse para resolver só uma coisa disso, qual seria?",
        "O que mudaria no seu resultado se isso estivesse resolvido?",
      ],
      dica: "Faz o cliente verbalizar o benefício. Quem fala, convence a si mesmo.",
    },
  ];

  const rapportTecnicas = [
    {
      nome: "Pesquisa de 2 minutos",
      texto: "Antes de discar, encontre um fato real: uma vaga aberta, uma expansão, um post recente, uma nota do setor. Rapport começa em referência específica, não em simpatia genérica.",
    },
    {
      nome: "Espelhamento de ritmo",
      texto: "Cliente que fala rápido e direto quer resposta rápida e direta. Cliente analítico quer dado e pausa. Adaptar o ritmo é mais eficaz do que qualquer frase pronta de conexão.",
    },
    {
      nome: "Escuta que devolve",
      texto: "Repita a dor com as palavras dele antes de apresentar solução: 'Então o problema não é o volume de leads, é o tempo de resposta, certo?'. Quando o cliente ouve a própria frase, ele confia.",
    },
    {
      nome: "Permissão explícita",
      texto: "'Posso te fazer três perguntas rápidas antes de falar qualquer coisa sobre a gente?' Pedir permissão transforma interrogatório em conversa — e derruba a defesa do primeiro minuto.",
    },
  ];

  const errosVSL = [
    "VSL que fala 8 minutos da empresa antes de citar o problema do espectador",
    "Vídeo bonito sem chamada clara para o próximo passo",
    "Enviar a VSL e não ligar depois — vídeo assistido é lead quente, não lead resolvido",
    "Reescrever a VSL do zero em vez de usar o script que já converte na ligação",
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Script de Vendas Pronto: o roteiro que gera reunião e fecha negócio",
    description:
      "Script de vendas pronto para preencher: estrutura de roteiro de ligação comercial, script de prospecção, técnicas de perguntas, rapport e uso de VSL. Download em .txt.",
    author: { "@type": "Person", name: "Junior Bianchim", jobTitle: "Especialista em Treinamento de Equipes Comerciais" },
    publisher: { "@type": "Organization", name: "Bianchim Business" },
    datePublished: "2026-08-25",
    dateModified: "2026-08-25",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://bianchimbusiness.com.br/posts/script-de-vendas-pronto",
    },
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "O que é um script de vendas?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Script de vendas é a estrutura escrita da conversa comercial: abertura, rapport, perguntas de descoberta, apresentação da solução, tratamento de objeções e definição do próximo passo. Ele não deve ser lido palavra por palavra — serve para garantir que nenhuma etapa importante seja esquecida.",
        },
      },
      {
        "@type": "Question",
        name: "Como fazer um roteiro de ligação comercial que funciona?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Comece pedindo permissão em 15 segundos, cite um fato real sobre a empresa do cliente para criar rapport, faça perguntas de situação, problema e implicação antes de apresentar qualquer solução, trate objeções investigando a causa e encerre com um próximo passo com data e hora marcadas.",
        },
      },
      {
        "@type": "Question",
        name: "Script de prospecção engessa o vendedor?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Não quando é usado como estrutura e treinado em voz alta. O que engessa é ler o texto durante a ligação. Times que treinam o script em role-play semanal ganham naturalidade e usam o roteiro apenas como trilho da conversa.",
        },
      },
      {
        "@type": "Question",
        name: "VSL substitui o script de vendas?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Não. A VSL (Video Sales Letter) padroniza a etapa de apresentação e educa o lead antes da conversa, mas a descoberta, o rapport e o fechamento continuam sendo humanos. O melhor uso é a VSL aquecer e o script conduzir a conversa que fecha.",
        },
      },
    ],
  };

  const relatedPosts = [
    {
      title: "13 Estratégias de Vendas Para Dominar Qualquer Negociação em 2026",
      readTime: "15 min",
      link: "/posts/13-estrategias-vendas-2026",
      image: postCoverEstrategias,
    },
    {
      title: "Cold Calling em 2026: Guia Completo Para Prospecção Ativa",
      readTime: "12 min",
      link: "/posts/cold-calling-2026",
      image: postCoverColdcalling,
    },
    {
      title: "Objeções de Preço: Como Transformar em Oportunidade",
      readTime: "8 min",
      link: "/posts/objecoes-preco",
      image: postCoverObjecoes,
    },
  ];

  return (
    <>
      <SEOHead
        title="Script de Vendas Pronto Para Preencher (Download Grátis em TXT)"
        description="Script de vendas pronto para baixar e preencher: roteiro de ligação comercial, script de prospecção, técnicas de perguntas, rapport e VSL. Modelo em .txt grátis."
        keywords="script de vendas, script de vendas pronto, roteiro de ligação comercial, script de prospecção, modelo de script de vendas, script de vendas por telefone, VSL vendas, técnicas de perguntas em vendas, rapport em vendas"
        canonicalUrl="https://bianchimbusiness.com.br/posts/script-de-vendas-pronto"
        ogType="article"
        articlePublishedTime="2026-08-25"
        articleAuthor="Junior Bianchim"
      />

      <div className="min-h-screen bg-background">
        <Header />

        <main>
          <article>
            {/* Hero */}
            <section className="relative">
              <div className="w-full h-[50vh] sm:h-[60vh] relative overflow-hidden">
                <img
                  src={postCoverScript}
                  alt="Vendedor com headset segurando um script de vendas pronto para preencher"
                  className="w-full h-full object-cover"
                  width={1600}
                  height={912}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
              </div>
              <div className="container mx-auto px-4 relative -mt-32 sm:-mt-40 z-10">
                <div className="max-w-4xl mx-auto">
                  <span className="inline-block px-4 py-1.5 rounded-full bg-primary/20 text-primary text-sm font-semibold mb-4">
                    Processo Comercial
                  </span>
                  <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight mb-4 text-foreground">
                    Script de Vendas Pronto:{" "}
                    <span className="text-gradient-brand">o roteiro que abre porta, faz pergunta certa e marca o próximo passo</span>
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mt-6">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      <span>Junior Bianchim</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>12 min de leitura</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4" />
                      <span>25 Ago 2026</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Introdução */}
            <section className="py-10 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto prose prose-lg max-w-none text-muted-foreground space-y-6">
                  <p className="text-xl leading-relaxed">
                    <strong className="text-foreground">A maioria das equipes comerciais não tem problema de esforço. Tem problema de método.</strong>{" "}
                    Liga muito, fala bem, conhece o produto de cor — e mesmo assim ouve "me manda por e-mail" o dia inteiro. Quase sempre a
                    causa é a mesma: cada vendedor conduz a conversa de um jeito, dependendo do humor do dia.
                  </p>
                  <p>
                    Um <strong className="text-foreground">script de vendas</strong> resolve isso. Não porque transforma gente em robô, mas
                    porque tira da improvisação as decisões que não deveriam ser improvisadas: por onde começar, o que perguntar, quando
                    apresentar, como responder ao "está caro" e como sair da conversa com data marcada.
                  </p>
                  <p>
                    Neste guia você vai ver o que separa um roteiro de ligação comercial que gera reunião de um que gera desligamento, como
                    usar VSL sem virar refém do vídeo, quais técnicas de perguntas fazem o cliente vender para si mesmo e como construir
                    rapport de verdade. No fim, você baixa um <strong className="text-foreground">script de vendas pronto em .txt</strong>{" "}
                    para preencher com a realidade da sua empresa.
                  </p>
                </div>
              </div>
            </section>

            {/* Seção 1 — verdades em cards */}
            <section className="py-12 bg-muted/30">
              <div className="container mx-auto px-4">
                <div className="max-w-5xl mx-auto">
                  <h2 className="text-3xl md:text-4xl font-bold mb-3 text-foreground">
                    O que é (e o que não é) um bom script de vendas
                  </h2>
                  <p className="text-muted-foreground mb-8 max-w-3xl">
                    Antes do modelo, quatro princípios que definem se o seu roteiro vai ajudar ou atrapalhar o vendedor no telefone.
                  </p>
                  <div className="grid md:grid-cols-2 gap-6">
                    {verdades.map((v) => (
                      <Card key={v.title} className="p-6 border-border bg-card hover:border-primary/40 transition-colors">
                        <div className="w-11 h-11 rounded-xl bg-primary/15 flex items-center justify-center mb-4">
                          <v.icon className="w-5 h-5 text-primary" />
                        </div>
                        <h3 className="text-lg font-bold text-foreground mb-2">{v.title}</h3>
                        <p className="text-muted-foreground text-sm leading-relaxed">{v.text}</p>
                      </Card>
                    ))}
                  </div>

                  <Card className="mt-8 p-6 sm:p-8 border-primary/30 bg-gradient-to-br from-card to-primary/5">
                    <Quote className="w-8 h-8 text-primary mb-3" />
                    <p className="text-lg sm:text-xl text-foreground font-medium leading-relaxed">
                      "Script bom não é aquele que o vendedor decora. É aquele que ele consegue esquecer, porque já virou reflexo."
                    </p>
                    <p className="text-sm text-muted-foreground mt-3">Junior Bianchim</p>
                  </Card>
                </div>
              </div>
            </section>

            {/* Seção 2 — anatomia em tabela */}
            <section className="py-12 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-5xl mx-auto">
                  <h2 className="text-3xl md:text-4xl font-bold mb-3 text-foreground">
                    A anatomia de um roteiro de ligação comercial
                  </h2>
                  <p className="text-muted-foreground mb-8 max-w-3xl">
                    Seis blocos, tempos de referência e o erro que mais destrói cada um deles. Serve para telefone, WhatsApp e reunião online.
                  </p>

                  <div className="overflow-x-auto rounded-xl border border-border">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/60">
                        <tr>
                          <th className="text-left p-4 font-semibold text-foreground">Bloco</th>
                          <th className="text-left p-4 font-semibold text-foreground">Tempo</th>
                          <th className="text-left p-4 font-semibold text-foreground">Objetivo</th>
                          <th className="text-left p-4 font-semibold text-foreground">Erro comum</th>
                        </tr>
                      </thead>
                      <tbody>
                        {blocos.map((b, i) => (
                          <tr key={b.bloco} className={i % 2 ? "bg-muted/20" : ""}>
                            <td className="p-4 font-semibold text-primary whitespace-nowrap">{b.bloco}</td>
                            <td className="p-4 text-muted-foreground whitespace-nowrap">{b.tempo}</td>
                            <td className="p-4 text-muted-foreground">{b.objetivo}</td>
                            <td className="p-4 text-muted-foreground">{b.erroComum}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 items-center mt-10">
                    <img
                      src={scriptLigacao}
                      alt="Vendedor usando roteiro de ligação comercial durante uma chamada de prospecção"
                      className="rounded-xl w-full h-auto"
                      loading="lazy"
                      width={1280}
                      height={720}
                    />
                    <div>
                      <h3 className="text-2xl font-bold text-foreground mb-3">Script de prospecção é diferente de script de fechamento</h3>
                      <p className="text-muted-foreground mb-4">
                        Na prospecção, o objetivo não é vender: é conquistar 20 minutos de agenda. Por isso o roteiro é curto, tem uma dor,
                        uma prova e um pedido claro. Já na reunião de fechamento, o script é quase todo composto de perguntas — porque a
                        informação que fecha o negócio está com o cliente, não com você.
                      </p>
                      <p className="text-muted-foreground">
                        Se você quer se aprofundar nos modelos que sustentam cada etapa (SPIN, BANT, MEDDIC, CHAMP e outros), leia o guia
                        completo:{" "}
                        <Link to="/posts/13-estrategias-vendas-2026" className="text-primary font-semibold hover:underline">
                          13 Estratégias de Vendas Para Dominar Qualquer Negociação
                        </Link>
                        .
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Seção 3 — CTA com download do script */}
            <section className="py-14 bg-muted/30">
              <div className="container mx-auto px-4">
                <Card className="max-w-4xl mx-auto p-8 sm:p-10 border-primary/30 bg-gradient-to-br from-card via-primary/5 to-accent/10">
                  <div className="text-center mb-8">
                    <Download className="w-10 h-10 text-primary mx-auto mb-4" />
                    <h2 className="text-3xl md:text-4xl font-bold mb-3">
                      <span className="text-gradient-brand">Baixe o script de vendas pronto para preencher</span>
                    </h2>
                    <p className="text-muted-foreground max-w-2xl mx-auto">
                      Arquivo .txt com abertura, rapport, perguntas de descoberta, apresentação, respostas para as quatro objeções mais
                      comuns, fechamento e follow-up — com os campos entre colchetes para você adaptar à sua empresa. Informe seu e-mail e
                      o download começa na hora.
                    </p>
                  </div>

                  <form onSubmit={baixarScript} className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
                    <Input
                      type="text"
                      placeholder="Seu nome (opcional)"
                      value={nome}
                      onChange={(e) => setNome(e.target.value)}
                      aria-label="Seu nome"
                    />
                    <Input
                      type="text"
                      placeholder="Sua empresa (opcional)"
                      value={empresa}
                      onChange={(e) => setEmpresa(e.target.value)}
                      aria-label="Sua empresa"
                    />
                    <Input
                      type="email"
                      required
                      placeholder="Seu melhor e-mail *"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      aria-label="Seu e-mail"
                      className="sm:col-span-2"
                    />
                    <Button
                      type="submit"
                      size="lg"
                      disabled={saving}
                      className="sm:col-span-2 bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow text-base py-6 font-semibold"
                    >
                      {saving ? "Liberando..." : baixado ? "Baixar novamente o script (.txt)" : "Quero meu script de vendas em .txt"}
                      <Download className="ml-2 w-5 h-5" />
                    </Button>
                  </form>
                  <p className="text-xs text-muted-foreground text-center mt-4">
                    Usamos seu e-mail apenas para enviar conteúdos de vendas da Bianchim Business. Sem spam, cancelamento a qualquer momento.
                  </p>
                </Card>
              </div>
            </section>

            {/* Seção 4 — VSL */}
            <section className="py-12 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-5xl mx-auto">
                  <div className="flex items-center gap-3 mb-3">
                    <PlayCircle className="w-7 h-7 text-primary" />
                    <h2 className="text-3xl md:text-4xl font-bold text-foreground">VSL: o script que trabalha enquanto você dorme</h2>
                  </div>
                  <p className="text-muted-foreground mb-8 max-w-3xl">
                    VSL (Video Sales Letter) é a sua argumentação gravada em vídeo. Ela não substitui o vendedor: substitui a parte
                    repetitiva da conversa — aquela em que você explica pela milésima vez o que faz, para quem serve e qual resultado
                    entrega. Bem usada, ela chega na reunião com o lead já convencido do problema.
                  </p>

                  <div className="grid md:grid-cols-3 gap-6 mb-8">
                    <Card className="p-6 border-border bg-card">
                      <span className="text-xs font-semibold text-primary uppercase tracking-wide">Estrutura</span>
                      <h3 className="text-lg font-bold text-foreground mt-2 mb-2">Dor → prova → oferta → passo</h3>
                      <p className="text-sm text-muted-foreground">
                        Primeiros 30 segundos falando da dor do espectador, depois um caso real com número, então a oferta em uma frase e
                        um único próximo passo. Nada de currículo da empresa no começo.
                      </p>
                    </Card>
                    <Card className="p-6 border-border bg-card">
                      <span className="text-xs font-semibold text-primary uppercase tracking-wide">Duração</span>
                      <h3 className="text-lg font-bold text-foreground mt-2 mb-2">3 a 8 minutos, sem enrolação</h3>
                      <p className="text-sm text-muted-foreground">
                        Para prospecção fria, vídeos de 60 a 90 segundos personalizados performam melhor. Para lead que já pediu proposta,
                        a VSL mais longa faz sentido — desde que cada minuto responda a uma objeção.
                      </p>
                    </Card>
                    <Card className="p-6 border-border bg-card">
                      <span className="text-xs font-semibold text-primary uppercase tracking-wide">Uso no funil</span>
                      <h3 className="text-lg font-bold text-foreground mt-2 mb-2">Antes ou depois da conversa</h3>
                      <p className="text-sm text-muted-foreground">
                        Antes: aquece e filtra quem realmente tem a dor. Depois: reforça a proposta para os outros decisores que não estavam
                        na reunião. Nos dois casos, o vendedor liga em seguida — vídeo assistido é sinal de compra.
                      </p>
                    </Card>
                  </div>

                  <Card className="p-6 border-destructive/30 bg-destructive/5">
                    <div className="flex items-center gap-2 mb-4">
                      <AlertTriangle className="w-5 h-5 text-destructive" />
                      <h3 className="text-lg font-bold text-foreground">Erros que matam uma VSL</h3>
                    </div>
                    <ul className="space-y-2">
                      {errosVSL.map((e) => (
                        <li key={e} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <ChevronRight className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
                          <span>{e}</span>
                        </li>
                      ))}
                    </ul>
                  </Card>
                </div>
              </div>
            </section>

            {/* Seção 5 — técnicas de perguntas */}
            <section className="py-12 sm:py-16 bg-muted/30">
              <div className="container mx-auto px-4">
                <div className="max-w-5xl mx-auto">
                  <h2 className="text-3xl md:text-4xl font-bold mb-3 text-foreground">
                    Técnicas de perguntas: onde o script realmente vende
                  </h2>
                  <p className="text-muted-foreground mb-8 max-w-3xl">
                    Quem pergunta conduz. As quatro famílias abaixo formam a espinha dorsal da descoberta — e devem estar escritas no seu
                    script antes de qualquer frase sobre o produto.
                  </p>

                  <div className="grid md:grid-cols-2 gap-6">
                    {perguntas.map((p) => (
                      <Card key={p.tipo} className={`p-6 border-border bg-gradient-to-br ${p.cor}`}>
                        <h3 className="text-xl font-bold text-foreground mb-4">Perguntas de {p.tipo}</h3>
                        <ul className="space-y-3 mb-4">
                          {p.exemplos.map((ex) => (
                            <li key={ex} className="flex items-start gap-2 text-sm text-foreground">
                              <MessageSquare className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                              <span className="italic">"{ex}"</span>
                            </li>
                          ))}
                        </ul>
                        <p className="text-xs text-muted-foreground border-t border-border/60 pt-3">{p.dica}</p>
                      </Card>
                    ))}
                  </div>

                  <Card className="mt-8 p-6 border-primary/30 bg-card">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-primary mt-1 shrink-0" />
                      <p className="text-muted-foreground text-sm">
                        <strong className="text-foreground">Regra prática:</strong> depois de cada pergunta, conte até três em silêncio. A
                        resposta mais valiosa quase sempre vem depois da primeira pausa — e some quando o vendedor atropela o cliente com a
                        próxima frase do roteiro.
                      </p>
                    </div>
                  </Card>
                </div>
              </div>
            </section>

            {/* Seção 6 — rapport */}
            <section className="py-12 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-5xl mx-auto">
                  <div className="grid md:grid-cols-2 gap-8 items-center mb-10">
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <Handshake className="w-7 h-7 text-primary" />
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground">Rapport não é simpatia</h2>
                      </div>
                      <p className="text-muted-foreground">
                        Rapport é a sensação, no cliente, de que aquela conversa vale o tempo dele. Isso não nasce de piada nem de "tudo bem
                        com você?": nasce de referência específica, ritmo adequado e escuta que devolve o que foi dito. É a parte do script
                        que você não escreve palavra por palavra — escreve como comportamento.
                      </p>
                    </div>
                    <img
                      src={scriptRapport}
                      alt="Duas pessoas construindo rapport em uma reunião comercial"
                      className="rounded-xl w-full h-auto"
                      loading="lazy"
                      width={1280}
                      height={720}
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    {rapportTecnicas.map((r) => (
                      <Card key={r.nome} className="p-6 border-border bg-card hover:border-primary/40 transition-colors">
                        <h3 className="text-lg font-bold text-foreground mb-2">{r.nome}</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">{r.texto}</p>
                      </Card>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Seção 7 — treinar o script */}
            <section className="py-12 sm:py-16 bg-muted/30">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto text-center">
                  <Phone className="w-9 h-9 text-primary mx-auto mb-4" />
                  <h2 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">
                    Script guardado na gaveta não vende. Script treinado, sim.
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    Depois de preencher o modelo, leia em voz alta, grave a própria voz, corte tudo que soar artificial e treine as três
                    objeções que mais derrubam suas vendas até virarem resposta automática. Uma sessão curta por semana muda mais resultado
                    do que um treinamento anual de oito horas.
                  </p>
                  <p className="text-muted-foreground">
                    No Vendas Trainer, cada vendedor treina esse script com personas de cliente em IA que reagem como o mercado real,
                    recebe nota por critério — descoberta, rapport, contorno de objeção e fechamento — e o gestor acompanha a evolução de
                    cada pessoa em um painel.
                  </p>
                  <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
                    <Button
                      size="lg"
                      className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 text-base font-semibold"
                      asChild
                    >
                      <Link to="/auth">Criar conta grátis e treinar o script</Link>
                    </Button>
                    <span className="text-sm text-muted-foreground">Teste 7 dias sem pagar nada.</span>
                  </div>
                </div>
              </div>
            </section>

            {/* FAQ */}
            <section className="py-12 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-3xl md:text-4xl font-bold mb-8 text-foreground">Perguntas frequentes sobre script de vendas</h2>
                  <div className="space-y-4">
                    {faqJsonLd.mainEntity.map((f) => (
                      <Card key={f.name} className="p-6 border-border bg-card">
                        <h3 className="text-lg font-bold text-foreground mb-2">{f.name}</h3>
                        <p className="text-muted-foreground text-sm leading-relaxed">{f.acceptedAnswer.text}</p>
                      </Card>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Autor */}
            <section className="py-8">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <Card className="p-6 sm:p-8 border-border bg-card">
                    <div className="flex flex-col sm:flex-row gap-6 items-start">
                      <img
                        src={juniorBianchim}
                        alt="Junior Bianchim, especialista em treinamento de equipes comerciais"
                        className="w-24 h-24 rounded-full object-cover"
                        loading="lazy"
                        width={96}
                        height={96}
                      />
                      <div>
                        <h3 className="text-xl font-bold text-foreground mb-1">Junior Bianchim</h3>
                        <p className="text-primary font-medium text-sm mb-3">Especialista em Treinamento de Equipes Comerciais</p>
                        <p className="text-muted-foreground text-sm">
                          Com mais de 20 anos de experiência em vendas B2B e B2C, Junior já estruturou operações comerciais em empresas como
                          Citróleo Group, Latina Eletrodomésticos, Mapfre Seguros e Zentai Benefícios. Fundador do Vendas Trainer, plataforma
                          de IA para treinamento de SDR, BDR e closers.
                        </p>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </section>

            {/* Relacionados */}
            <section className="py-12">
              <div className="container mx-auto px-4">
                <div className="max-w-5xl mx-auto">
                  <h2 className="text-2xl font-bold mb-6 text-foreground">Continue lendo</h2>
                  <div className="grid md:grid-cols-3 gap-6">
                    {relatedPosts.map((p) => (
                      <Link key={p.link} to={p.link} className="block">
                        <Card className="overflow-hidden border-border bg-card hover:border-primary/40 transition-all h-full">
                          <img src={p.image} alt={p.title} className="w-full h-40 object-cover" loading="lazy" width={640} height={360} />
                          <div className="p-5">
                            <h3 className="font-bold text-foreground mb-2 text-sm">{p.title}</h3>
                            <span className="text-xs text-muted-foreground">{p.readTime} de leitura</span>
                          </div>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* CTA final */}
            <section className="py-16 sm:py-20 bg-muted/30">
              <div className="container mx-auto px-4">
                <Card className="p-8 sm:p-12 bg-gradient-to-br from-card via-primary/5 to-accent/10 border-primary/30 text-center max-w-4xl mx-auto">
                  <Zap className="w-10 h-10 text-primary mx-auto mb-4" />
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">
                    <span className="text-gradient-brand">Transforme seu script em conversa natural</span>
                  </h2>
                  <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                    Treine abertura, perguntas, rapport e fechamento com personas de cliente em IA, receba nota por critério e acompanhe a
                    evolução do time em um painel. <strong className="text-foreground">Comece grátis hoje mesmo.</strong>
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button
                      size="lg"
                      onClick={() => navigate("/auth?mode=signup")}
                      className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow text-lg px-8 py-6 font-semibold"
                    >
                      Criar Conta Grátis
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      onClick={() => navigate("/blog")}
                      className="text-lg px-8 py-6 border-primary/40"
                    >
                      Ver mais conteúdos
                    </Button>
                  </div>
                </Card>
              </div>
            </section>
          </article>

          <PageNavigation />
        </main>

        <Footer />
      </div>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
    </>
  );
};

export default ScriptDeVendasPronto;
